import { catError } from "./catLog";
import { get, set, del, keys, entries } from 'idb-keyval';

/** Normalize values restored from IndexedDB into Blob (some browsers return plain objects). */
export const normalizeStoredBlob = (value, fallbackType = 'application/octet-stream') => {
  if (!value) return null;
  if (value instanceof Blob) return value;
  if (value instanceof ArrayBuffer) return new Blob([value], { type: fallbackType });
  if (ArrayBuffer.isView(value)) return new Blob([value], { type: fallbackType });
  if (typeof value === 'object' && value.data) {
    const data = value.data;
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      return new Blob([data], { type: value.type || fallbackType });
    }
  }
  return null;
};

/**
 * v4.171.0: the on-call strip keeps a copy of every clip in memory, so it has to
 * be told when the audio underneath it changes. Fired from the one place all
 * recording paths (Studio, Greeting Editor, backup import) already pass through.
 */
export const SOUNDBOARD_CHANGED_EVENT = 'catint_soundboard_changed';
const announceSoundboardChange = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SOUNDBOARD_CHANGED_EVENT));
};

export const saveFile = async (key, fileOrBlob) => {
  try {
    await set(key, fileOrBlob);
    announceSoundboardChange();
    return true;
  } catch (err) {
    catError(`[Storage:save] Failed to save ${key} to IndexedDB:`, err);
    return false;
  }
};

export const loadFile = async (key) => {
  try {
    const raw = await get(key);
    return normalizeStoredBlob(raw);
  } catch (err) {
    catError(`[Storage:load] Failed to load ${key} from IndexedDB:`, err);
    return null;
  }
};

/**
 * Raw value exactly as stored (no blob normalization). Lets callers tell
 * "key holds a non-audio value" (captions array, archive object) apart from
 * "recording key whose blob vanished". Returns undefined on miss/error.
 */
export const loadRawValue = async (key) => {
  try {
    return await get(key);
  } catch (err) {
    catError(`[Storage:read] Failed to read ${key} from IndexedDB:`, err);
    return undefined;
  }
};

export const deleteFile = async (key) => {
  try {
    await del(key);
    announceSoundboardChange();
    return true;
  } catch (err) {
    catError(`[Storage:delete] Failed to delete ${key} from IndexedDB:`, err);
    return false;
  }
};

export const listStorageKeys = async () => {
  try {
    return await keys();
  } catch (err) {
    catError('[Storage:list] Failed to list IndexedDB keys:', err);
    return [];
  }
};

/** v4.105.0: all blob entries as [key, Blob] — feeds the per-file disk backup. */
export const getAllFileEntries = async () => {
  try {
    const pairs = await entries();
    const out = [];
    for (const [key, value] of pairs) {
      const blob = normalizeStoredBlob(value);
      if (blob) out.push([String(key), blob]);
    }
    return out;
  } catch (err) {
    catError('[Storage:scan] Failed to read IndexedDB entries:', err);
    return [];
  }
};

export const getStorageSummary = async () => {
  const allKeys = await listStorageKeys();
  const pairs = await entries();
  let bytes = 0;
  for (const [, value] of pairs) {
    const blob = normalizeStoredBlob(value);
    if (blob) bytes += blob.size;
  }
  return {
    keyCount: allKeys.length,
    keys: allKeys.map(String),
    bytes,
    origin: typeof window !== 'undefined' ? window.location.origin : '',
  };
};

export const exportStorageBackup = async () => {
  const pairs = await entries();
  const payload = { v: 1, exportedAt: new Date().toISOString(), items: {} };
  for (const [key, value] of pairs) {
    const blob = normalizeStoredBlob(value);
    if (!blob) continue;
    const buf = await blob.arrayBuffer();
    payload.items[String(key)] = {
      type: blob.type || 'application/octet-stream',
      data: Array.from(new Uint8Array(buf)),
    };
  }
  return payload;
};

export const importStorageBackup = async (payload) => {
  if (!payload?.items || typeof payload.items !== 'object') {
    throw new Error('Invalid backup file');
  }
  let count = 0;
  for (const [key, item] of Object.entries(payload.items)) {
    if (!item?.data) continue;
    const blob = new Blob([Uint8Array.from(item.data)], { type: item.type || 'application/octet-stream' });
    await set(key, blob);
    count += 1;
  }
  // v4.171.0: a restored backup is new audio — the on-call strip must re-scan.
  announceSoundboardChange();
  return count;
};

export const generateObjectUrl = (blob) => {
  if (!blob) return null;
  return URL.createObjectURL(blob);
};
